import React, { useContext, useRef, useEffect } from 'react'
import { Carousel, Popover } from 'antd'
import FrameSizeItem from './FrameSizeItem'
import { OrderFormContext } from '../../contexts'
import { isProductWithLongSizeLabel, needCroppingFunc } from '../../utils/helper'

const FrameSizeSelection = () => {
  const orderFormContext = useContext(OrderFormContext)

  const mainRef = useRef(null)

  const {
    product,
    frameSize,
    setFrameSize,
    availableFrameSizes,
    clearSelectedAttributes,
    selectedShape,
    hoveredFrameSize,
    showHoveredFrameSize,
    setImages
  } = orderFormContext

  useEffect(() => {
    if (!!availableFrameSizes && !frameSize) {
      let firstSize = null

      availableFrameSizes.forEach(frameGroup => {
        const sizes = frameGroup.options.filter((frame) => !selectedShape || frame.shape === selectedShape)

        const hasMoreThanOneSize = sizes.length > 1
        firstSize = !hasMoreThanOneSize ? sizes[0] : null
      })

      if (firstSize) setFrameSize(firstSize)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableFrameSizes, selectedShape])

  const onSelectFrameSize = frameSize => {
    clearSelectedAttributes()
    setFrameSize(frameSize)
  }

  /**
   * Use effect - detect frame-size change to update crop feature with orientation logic
   */
  React.useEffect(() => {
    if (frameSize) {
      const croppedAreaAspect = frameSize ? (Number(frameSize.frame_height) / Number(frameSize.frame_width)) : null
      const newValue = []
      setImages(images => {
        images.forEach(image => {
          const croppedArea = image.croppedArea || {}
          let isFlippedRatio = croppedArea.isFlippedRatio || false
          let needCrop = needCroppingFunc(croppedArea, image.original, croppedAreaAspect)
          if (needCrop) {
            croppedArea.isFlippedRatio = !isFlippedRatio
            needCrop = needCroppingFunc(croppedArea, image.original, croppedAreaAspect)
            if (!needCrop) {
              const frameHeight = Number(frameSize.frame_height)
              const frameWidth = Number(frameSize.frame_width)
              isFlippedRatio = !isFlippedRatio
              croppedArea.isFlippedRatio = isFlippedRatio
              croppedArea.x = 0
              croppedArea.y = 0
              croppedArea.width = 100
              croppedArea.height = 100
              croppedArea.ratioInfo = {
                ...(croppedArea.ratioInfo || {}),
                isFlippedRatio,
                h: frameHeight,
                w: frameWidth,
                originalRatioStr: frameSize.frame_aspect_ratio,
                originalRatioNum: [frameHeight, frameWidth],
                renderedRatioStr: isFlippedRatio ? `${frameWidth}:${frameHeight}` : `${frameHeight}:${frameWidth}`,
                renderedRatioNum: isFlippedRatio ? [frameWidth, frameHeight] : [frameHeight, frameWidth],
              }
            }
          }
          newValue.push({
            ...image,
            croppedArea,
          })
        })
        return newValue
      })
    }
  }, [frameSize, setImages])

  const FrameSizes = availableFrameSizes.map((frameGroup, index) => {
    const numberOfCols = isProductWithLongSizeLabel(product) ? 2 : 4

    const renderFrameByShape = (shape) => (
      <div className={`SizeSwatches has-${numberOfCols}-columns`}>
        {frameGroup.options.map((frame) => {
          const isInShape = !shape || frame.shape === shape
          const isMatchSizeId = frameSize && frameSize.id === frame.id
          const isSelectedShape = (!frameSize || !shape) || frameSize.shape === shape
          const isSelected = isInShape && isMatchSizeId && isSelectedShape

          let isGrayOut = false
          if (frameSize !== null && !isSelected) {
            isGrayOut = true
          }

          return isInShape && (
            <FrameSizeItem
              key={`${frame.id}-${frame.shape}`}
              isGrayOut={isGrayOut}
              isSelected={isSelected}
              onSelect={() => onSelectFrameSize(frame)}
              {...frame}
            />
          )
        })}
      </div>
    )

    return (
      <div className="FrameSizeGroup" key={`groupIndex-${index}`}>
        <h4 className="FrameSizeGroup__Heading">
          {frameGroup.title}*
          <span className="frame-title">{frameSize && ` ${frameSize.title}`}</span>
        </h4>
        {renderFrameByShape(selectedShape)}
      </div>
    )
  })

  const details = () => {
    const hasImages = !!hoveredFrameSize.images
    const images = hasImages ? JSON.parse(hoveredFrameSize.images) : []
    let popupClassBasedOnImages = ''

    if (images.length > 0) {
      popupClassBasedOnImages = 'simple-layout'
    }

    return (
      <div className={`AttributePopup ${popupClassBasedOnImages}`}>
        <Carousel autoplay effect="fade" autoplaySpeed={2000}>
          { images.map(image => (
            <img alt={''} key={image} src={image} className="popup-thumbnail"/>
          ))}
        </Carousel>
        <div className="popup-body">
          <span className="popup-content">{hoveredFrameSize.description}</span>
          <span className="popup-content">${hoveredFrameSize.price}</span>
        </div>
      </div>
    )
  }

  return (
    <Popover content={details()} placement="left" visible={showHoveredFrameSize} autoAdjustOverflow={false}>
      <div className="FrameSizeSelection">
        <div className="FrameSizeSelectionWrapper" ref={mainRef}>
          {FrameSizes}
        </div>
      </div>
    </Popover>
  )
}

export default FrameSizeSelection
